import React from 'react';

interface TechnicalArchitectureVisualizationProps {
  architectureText: string;
  className?: string;
}

interface ArchitectureItem {
  title: string;
  description: string;
}

const TechnicalArchitectureVisualization: React.FC<TechnicalArchitectureVisualizationProps> = ({ 
  architectureText, 
  className = '' 
}) => {

  // Pastel color palette for the boxes
  const pastelColors = [
    'bg-blue-50 border-blue-200 hover:border-blue-300',
    'bg-purple-50 border-purple-200 hover:border-purple-300',
    'bg-green-50 border-green-200 hover:border-green-300',
    'bg-orange-50 border-orange-200 hover:border-orange-300',
    'bg-pink-50 border-pink-200 hover:border-pink-300',
    'bg-indigo-50 border-indigo-200 hover:border-indigo-300',
    'bg-teal-50 border-teal-200 hover:border-teal-300',
    'bg-amber-50 border-amber-200 hover:border-amber-300',
  ];

  // Parse the architecture text to extract items
  const parseArchitecture = (text: string): ArchitectureItem[] => {
    const items: ArchitectureItem[] = [];
    
    // Remove "Architecture" headers that appear at the start
    let cleanedText = text.replace(/^Architecture\s*Architecture\s*/i, '');
    
    // Split by lines and match patterns like "- Title: Description"
    const lines = cleanedText.split('\n');
    
    for (const line of lines) {
      // Match pattern: "- Title: Description" or "- Title - Description"
      const match = line.match(/^-\s*([^:-]+)[:|-]\s*(.+)$/);
      if (match) {
        const title = match[1].trim();
        const description = match[2].trim();
        
        if (title && description) {
          items.push({ title, description });
        }
      }
    }
    
    return items;
  };

  const items = parseArchitecture(architectureText);

  if (items.length === 0) {
    // Fallback to plain text if parsing fails
    return (
      <div className={`text-gray-700 leading-relaxed whitespace-pre-line ${className}`}>
        {architectureText}
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Desktop View - Grid Layout */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item, index) => {
          const colorClass = pastelColors[index % pastelColors.length];
          
          return (
            <div
              key={index}
              className={`rounded-lg p-4 border transition-colors duration-200 min-h-[100px] ${colorClass}`}
            >
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                {item.title}
              </h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                {item.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Mobile View - Vertical List */}
      <div className="md:hidden space-y-3">
        {items.map((item, index) => {
          const colorClass = pastelColors[index % pastelColors.length];
          
          return (
            <div
              key={index}
              className={`rounded-lg p-4 border transition-colors duration-200 ${colorClass}`}
            >
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                {item.title}
              </h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                {item.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TechnicalArchitectureVisualization;
